import React, { useState, useEffect } from "react";
import { Location } from "../maps/types";
import GoogleMap from "../maps/GoogleMap";
import FallbackLeafletMap from "../maps/FallbackLeafletMap";
import LocationSearch from "../maps/LocationSearch";
import { useToast } from "@/hooks/use-toast";
import { MapPin } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface LocationPickerProps {
  initialLocation?: Location | null;
  onLocationSelected: (location: Location) => void;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  initialLocation,
  onLocationSelected,
}) => {
  const { toast } = useToast();
  const [currentLocation, setCurrentLocation] = useState<Location | null>(initialLocation || null);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [useGoogleMap, setUseGoogleMap] = useState(false);

  useEffect(() => {
    if (!currentLocation) {
      handleGetCurrentLocation();
    }
  }, []);

  useEffect(() => {
    if (showInstructions) {
      const timer = setTimeout(() => {
        setShowInstructions(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showInstructions]);

  useEffect(() => {
    if (currentLocation) {
      setShowInstructions(true);
    }
  }, [currentLocation]);

  useEffect(() => {
    console.log("LocationPicker - Using Google Maps:", useGoogleMap);
  }, [useGoogleMap]);

  const handleGetCurrentLocation = () => {
    if (isGettingCurrentLocation) return;
    
    setIsGettingCurrentLocation(true);
    
    if (navigator.geolocation) {
      toast({
        title: "Getting Your Location",
        description: "Please allow location access if prompted by your browser.",
      });
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = { 
            lat: latitude, 
            lng: longitude,
            name: "Your Current Location"
          };
          console.log("Got current location:", newLocation);
          
          handleLocationFound(newLocation);
          setIsGettingCurrentLocation(false);
        },
        (error) => {
          setIsGettingCurrentLocation(false);
          console.error("Geolocation error:", error);
          let errorMessage = "Unable to get your location";
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Location access was denied. Please check your browser settings.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable. Please try again later.";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out. Please try again.";
              break;
          }
          
          toast({
            title: "Location Error",
            description: errorMessage,
            variant: "destructive",
          });
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 0 
        }
      );
    } else {
      setIsGettingCurrentLocation(false);
      toast({
        title: "Geolocation Not Supported",
        description: "Your browser does not support geolocation services.",
        variant: "destructive",
      });
    }
  };

  const handleLocationFound = (location: Location) => {
    console.log("New location selected:", location);
    setCurrentLocation(location);
    onLocationSelected(location);
    
    const locationText = location.name || 
      `Coordinates (${location.lat.toFixed(4)}, ${location.lng.toFixed(4)})`;
    
    toast({
      title: "Location Set",
      description: `Location set to: ${locationText}`,
    });
  };

  const toggleMap = () => {
    console.log("Toggling map from", useGoogleMap ? "Google" : "Leaflet", "to", !useGoogleMap ? "Google" : "Leaflet");
    setUseGoogleMap(!useGoogleMap);
    toast({
      title: useGoogleMap ? "Using Alternative Map" : "Using Google Maps",
      description: useGoogleMap 
        ? "Switched to alternative map for location selection" 
        : "Switched to Google Maps",
    });
  };

  return (
    <div className="h-full flex flex-col">
      <Alert variant="default" className="rounded-none border-x-0 border-t-0 bg-muted/50">
        <MapPin className="h-4 w-4" />
        <AlertTitle>Set location for the crime report</AlertTitle>
        <AlertDescription>
          Search for a location, use your current location, or click directly on the map
        </AlertDescription>
      </Alert>
      
      <LocationSearch 
        onLocationFound={handleLocationFound}
        onUseCurrentLocation={handleGetCurrentLocation}
        onToggleFallbackMap={toggleMap}
        useFallbackMap={!useGoogleMap}
      />
      
      <div className="relative flex-1">
        {!useGoogleMap ? (
          <FallbackLeafletMap 
            crimeReports={[]} 
            isRecent={() => false}
            onLocationSelect={handleLocationFound}
            initialLocation={currentLocation}
          />
        ) : (
          <GoogleMap
            initialLocation={currentLocation}
            onLocationSelected={handleLocationFound}
            onUseFallbackMap={() => setUseGoogleMap(false)}
          />
        )}
        
        {showInstructions && currentLocation && useGoogleMap && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none z-10">
            <div className="bg-black/70 text-white px-4 py-3 rounded-lg flex items-center max-w-xs">
              <MapPin className="h-5 w-5 mr-2 flex-shrink-0" />
              <p className="text-sm">Click on the map or drag the marker to adjust the crime location</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationPicker;
